/**
 * CustomWebsocketProvider - WebSocket provider for custom Lambda Yjs sync
 *
 * Unlike y-websocket's WebsocketProvider which uses binary protocol,
 * this provider communicates with our Lambda using JSON messages:
 *
 * Client -> Server:
 *   { action: 'subscribe', roomId: string }
 *   { action: 'update', roomId: string, update: base64 }
 *   { action: 'awareness', roomId: string, awareness: object }
 *   { action: 'ping' }
 *
 * Server -> Client:
 *   { action: 'sync', type: 'initial', roomId: string, state: base64 | null }
 *   { action: 'sync', type: 'update', roomId: string, update: base64 }
 *   { action: 'awareness', roomId: string, data: object }
 *   { action: 'pong' }
 */

import * as Y from 'yjs';
import { Observable } from 'lib0/observable';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import { API } from 'aws-amplify';

// Telemetry schema version and frozen event types (Tightening #7)
const TELEMETRY_SCHEMA_VERSION = 'v2';

const TELEMETRY_EVENT_TYPES = {
  // Core update lifecycle
  UPDATE_PROCESSED: 'yjs_update_processed',      // Legacy, keep for continuity
  UPDATE_QUEUED: 'yjs_update_queued',
  UPDATE_SENT: 'yjs_update_sent',
  UPDATE_APPLIED: 'yjs_update_applied',          // Remote update applied to Y.Doc
  UPDATE_ACKED: 'yjs_update_acked',
  UPDATE_DISCARDED: 'yjs_update_discarded',
  PENDING_UPDATE_DISCARDED: 'yjs_pending_update_discarded',  // Batch discard event

  // ACK reliability
  ACK_TIMEOUT: 'yjs_ack_timeout',
  RETRY_EXHAUSTED: 'yjs_retry_exhausted',
  EXHAUSTED_RETRY_ATTEMPT: 'yjs_exhausted_retry_attempt',  // Retry after exhaustion

  // Queue management
  QUEUE_LIMIT_EVICTION: 'yjs_queue_limit_eviction',
  QUEUE_LIMIT_BLOCKED: 'yjs_queue_limit_blocked',    // Queue full of user-edits
  USER_EDIT_BLOCKED: 'yjs_user_edit_blocked',        // User edit rejected

  // Comment tracking (Phase 2)
  COMMENT_FIELD_CHANGED: 'yjs_comment_field_changed',

  // Connection lifecycle
  CONNECT: 'yjs_connect',
  DISCONNECT: 'yjs_disconnect',
  SYNC_START: 'yjs_sync_start',
  SYNC_COMPLETE: 'yjs_sync_complete',

  // Telemetry reliability (Phase 1)
  TELEMETRY_DROPPED: 'yjs_telemetry_dropped',
  TELEMETRY_QUEUED: 'yjs_telemetry_queued',
  TELEMETRY_SENT: 'yjs_telemetry_sent',

  // Legacy/compatibility events
  UPDATE_APPLIED_REMOTE: 'yjs_update_applied_remote',
  UPDATE_SEND_FAILED: 'yjs_update_send_failed',
  UPDATE_REPLAYED: 'yjs_update_replayed',
  UPDATE_REPLAY_FAILED: 'yjs_update_replay_failed',
  UPDATE_REPLAY_BLOCKED: 'yjs_update_replay_blocked',
  REPLAY_COMPLETE: 'yjs_replay_complete',
  REPLAY_PARTIAL: 'yjs_replay_partial',
  UNACKED_REQUEUED: 'yjs_unacked_requeued',
  PENDING_UPDATE_PRESERVED: 'yjs_pending_update_preserved',
  PENDING_QUEUE_RESTORED: 'yjs_pending_queue_restored',
  REFRESH_BLOCKED: 'yjs_refresh_blocked',
  COMMENT_CHANGE_DETECTED: 'yjs_comment_change_detected',
  ERROR: 'yjs_error',
  EXHAUSTED_UPDATE_EXPIRED: 'yjs_exhausted_update_expired',
  TELEMETRY_DELIVERY_SUMMARY: 'yjs_telemetry_delivery_summary',
} as const;

/**
 * Pending update with metadata for forensic tracing (Tightening #8)
 */
interface PendingUpdate {
  update: Uint8Array;
  origin: any;
  updateId: string;
  timestamp: number;
  clientTs: string;
  retryCount: number;  // Preserve across disconnect
}

interface UnackedUpdate extends PendingUpdate {
  sentAt: number;
  exhausted?: boolean;  // Marked when max retries exceeded (Tightening #1)
  exhaustedAt?: number; // Timestamp of exhaustion (Tightening #1)
}

export interface CustomWebsocketProviderOptions {
  /** WebSocket endpoint URL */
  wsEndpoint: string;
  /** Room ID for the document */
  roomId: string;
  /** Yjs document */
  ydoc: Y.Doc;
  /** JWT token for authentication (static - will expire if tab is backgrounded) */
  token?: string;
  /**
   * Function to get fresh JWT token (preferred over static token).
   * Called on every connect/reconnect to ensure token is not expired.
   * This fixes "Signature has expired" errors when tabs are backgrounded.
   */
  getToken?: () => Promise<string>;
  /** Auto-connect on creation (default: true) */
  connect?: boolean;
  /** Reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Max reconnect delay in ms (default: 30000) */
  maxReconnectDelay?: number;
  /** Ping interval in ms (default: 30000) */
  pingInterval?: number;
  /** Telemetry context (optional) */
  telemetry?: {
    clientId?: string;
    sheetId?: string;
    userId?: string;
    userEmail?: string;
  };
}

export class CustomWebsocketProvider extends Observable<string> {
  private wsEndpoint: string;
  private roomId: string;
  private ydoc: Y.Doc;
  private token: string;
  private getToken?: () => Promise<string>;
  private ws: WebSocket | null = null;
  private reconnectDelay: number;
  private maxReconnectDelay: number;
  private currentReconnectDelay: number;
  private pingInterval: number;
  private pingTimer: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private syncWatchdogTimer: NodeJS.Timeout | null = null;
  private shouldConnect: boolean;
  private _synced: boolean = false;
  private _connected: boolean = false;
  private awareness: Map<string, any> = new Map();
  private localAwarenessState: any = null;
  private destroyed: boolean = false;
  private telemetry?: {
    clientId?: string;
    sheetId?: string;
    userId?: string;
    userEmail?: string;
  };

  // Queue for updates made while disconnected - prevents data loss
  private pendingUpdates: PendingUpdate[] = [];
  // Updates sent over socket but not yet acknowledged by server
  private unackedUpdates: Map<string, UnackedUpdate> = new Map();
  private ackTimers: Map<string, NodeJS.Timeout> = new Map();

  // ACK protocol configuration (Tightenings #1, #8)
  private readonly ackTimeoutMs: number = 10000;  // Fixed 10s ACK wait per attempt
  private readonly MAX_SOFT_RETRIES = 2;   // Retries 1-2: soft (same socket)
  // Hard retry is retry #3 (MAX_SOFT_RETRIES + 1)
  private readonly MAX_TOTAL_RETRIES = 3;  // After 3 retries → exhausted

  // Queue limit constants (Tightening #3, #6)
  private readonly MAX_PENDING_UPDATES_COUNT = 1000;
  private readonly MAX_PENDING_UPDATES_BYTES = 10 * 1024 * 1024; // 10MB
  private readonly MAX_UNACKED_UPDATES_COUNT = 500;
  private readonly MAX_UNACKED_UPDATES_BYTES = 5 * 1024 * 1024; // 5MB

  // Queue full flag for blocking user edits (Tightening #6)
  private queueFull = false;

  // Track which exhausted updates already have scheduled retries (Bugfix P2)
  private exhaustedRetryScheduled = new Set<string>();

  // Persist pending/unacked queues to survive page refresh
  private queueDbName = 'yjs-offline-queue-v1';
  private queueStoreName = 'queues';
  private queuePersistTimer: NodeJS.Timeout | null = null;

  // Browser unload guard to prevent silent data loss
  private beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

  // Telemetry: track sync timing
  private syncStartTime: number = 0;

  // Comment tracking: cache for detecting changes to ourComment/notes
  private commentCache = new Map<string, { ourComment: string | null; notes: string | null }>();

  // Tracking IDs for telemetry and debugging
  private tabId: string;
  private sessionId: string;
  private updateCounter: number = 0;
  private eventSeq: number = 0;
  private telemetrySendFailures: number = 0;

  // Update handler to listen for local Yjs changes
  private updateHandler: (update: Uint8Array, origin: any) => void;

  private isForbiddenError(error: any): boolean {
    return error?.response?.status === 403 || error?.status === 403 || error?.code === 403;
  }

  constructor(options: CustomWebsocketProviderOptions) {
    super();

    this.wsEndpoint = options.wsEndpoint;
    this.roomId = options.roomId;
    this.ydoc = options.ydoc;
    this.token = options.token || '';
    this.getToken = options.getToken;
    this.reconnectDelay = options.reconnectDelay ?? 1000;
    this.maxReconnectDelay = options.maxReconnectDelay ?? 30000;
    this.currentReconnectDelay = this.reconnectDelay;
    this.pingInterval = options.pingInterval ?? 30000;
    this.shouldConnect = options.connect ?? true;
    this.telemetry = options.telemetry;

    // Generate unique IDs for telemetry tracking
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[YJS-WS] Initializing provider for room: ${this.roomId}, endpoint: ${this.wsEndpoint}`);
    console.log(`[YJS-WS] Initialized with tabId: ${this.tabId}, sessionId: ${this.sessionId}`);

    // Listen for local Yjs updates to broadcast
    this.updateHandler = (update: Uint8Array, origin: any) => {
      if (origin === 'remote') return;

      const updateId = this.generateUpdateId();
      const clientTs = new Date().toISOString();
      const action = (this._connected && this._synced) ? 'sent' : 'queued';

      // Keep legacy event for continuity while adding explicit lifecycle events.
      this.logTelemetry('yjs_update_processed', {
        updateId,
        clientTs,
        bytes: update.length,
        origin: String(origin),
        action,
        connected: this._connected,
        synced: this._synced,
        queueSize: this.pendingUpdates.length + (action === 'queued' ? 1 : 0)
      });

      if (this._connected && this._synced) {
        // Try to send immediately, but queue if send fails (socket closed mid-send)
        try {
          this.sendUpdate(update, {
            updateId,
            origin: String(origin),
            clientTs,
            retryCount: 0
          });
        } catch (error) {
          // Socket closed between check and send - queue it instead
          console.warn(`[YJS-WS] ⚠️ Send failed (${error}), queueing update: ${updateId}`);
          this.logTelemetry('yjs_update_send_failed', {
            updateId,
            clientTs,
            bytes: update.length,
            origin: String(origin),
            error: String(error),
            reason: 'socket_closed_during_send'
          });

          // Check queueFull flag before queuing user-edit (Tightening #6)
          if (this.queueFull && String(origin) === 'user-edit') {
            console.error(`[YJS-WS] ❌ User edit rejected - queue full of unsent user edits`);
            this.logTelemetry(TELEMETRY_EVENT_TYPES.USER_EDIT_BLOCKED, {
              updateId,
              bytes: update.length,
              queueCount: this.pendingUpdates.length,
              reason: 'queue_full'
            });
            this.showQueueFullWarning();
            return;  // Drop edit WITH WARNING
          }

          this.logTelemetry('yjs_update_queued', {
            updateId,
            clientTs,
            bytes: update.length,
            origin: String(origin),
            connected: this._connected,
            synced: this._synced,
            queueSize: this.pendingUpdates.length + 1,
            fallbackFromSendFailure: true
          });
          this.pendingUpdates.push({
            update,
            origin,
            updateId,
            timestamp: Date.now(),
            clientTs,
            retryCount: 0,
          });
          this.enforceQueueLimits();  // Enforce limits after queuing (Tightenings #3, #6)
          this.persistQueues();
        }
      } else {
        // Check queueFull flag before queuing user-edit (Tightening #6)
        if (this.queueFull && String(origin) === 'user-edit') {
          console.error(`[YJS-WS] ❌ User edit rejected - queue full of unsent user edits`);
          this.logTelemetry(TELEMETRY_EVENT_TYPES.USER_EDIT_BLOCKED, {
            updateId,
            bytes: update.length,
            queueCount: this.pendingUpdates.length,
            reason: 'queue_full'
          });
          this.showQueueFullWarning();
          return;  // Drop edit WITH WARNING
        }

        console.log(`[YJS-WS] 📋 Queuing local update (${update.length} bytes, origin: ${origin}, updateId: ${updateId}) - connected: ${this._connected}, synced: ${this._synced}, queue size: ${this.pendingUpdates.length + 1}`);
        this.logTelemetry('yjs_update_queued', {
          updateId,
          clientTs,
          bytes: update.length,
          origin: String(origin),
          connected: this._connected,
          synced: this._synced,
          queueSize: this.pendingUpdates.length + 1
        });
        this.pendingUpdates.push({
          update,
          origin,
          updateId,
          timestamp: Date.now(),
          clientTs,
          retryCount: 0,
        });
        this.enforceQueueLimits();  // Enforce limits after queuing (Tightenings #3, #6)
        this.persistQueues();
      }
    };
    this.ydoc.on('update', this.updateHandler);

    this.registerBeforeUnloadGuard();

    // Restore pending/unacked queues before first connect.
    // This allows replay of unsent edits after refresh.
    void this.loadPersistedQueues().finally(() => {
      if (this.shouldConnect) {
        this.connect();
      }
    });
  }

  get synced(): boolean {
    return this._synced;
  }

  get connected(): boolean {
    return this._connected;
  }

  /**
   * Generate unique update ID for tracking individual updates through their lifecycle
   */
  private generateUpdateId(): string {
    return `upd_${this.tabId}_${++this.updateCounter}`;
  }

  /**
   * Check if queues have capacity for new updates (Bugfix Round 2 #2)
   * Uses hard limits (100%) for recovery check - users should be unblocked as soon as possible
   */
  private hasCapacity(): boolean {
    const pendingBytes = this.pendingUpdates.reduce((sum, p) => sum + p.update.length, 0);
    const unackedBytes = Array.from(this.unackedUpdates.values())
      .reduce((sum, u) => sum + u.update.length, 0);

    return (
      this.pendingUpdates.length < this.MAX_PENDING_UPDATES_COUNT &&
      pendingBytes < this.MAX_PENDING_UPDATES_BYTES &&
      this.unackedUpdates.size < this.MAX_UNACKED_UPDATES_COUNT &&
      unackedBytes < this.MAX_UNACKED_UPDATES_BYTES
    );
  }

  /**
   * Mark update as exhausted (Tightening #1)
   * Called when max retries exceeded - keeps update in queue instead of discarding
   */
  private markAsExhausted(updateId: string, update: UnackedUpdate): void {
    console.error(`[YJS-WS] ❌ Update ${updateId} retry exhausted (kept in queue)`);

    this.clearAckTimer(updateId);
    this.unackedUpdates.set(updateId, {
      ...update,
      exhausted: true,
      exhaustedAt: Date.now()
    });

    this.logTelemetry(TELEMETRY_EVENT_TYPES.RETRY_EXHAUSTED, {
      updateId,
      origin: String(update.origin),
      retryCount: update.retryCount,
      maxRetries: this.MAX_TOTAL_RETRIES,
      action: 'kept_in_queue'
    });

    this.persistQueues();
  }

  /**
   * Enforce queue size limits with smart priority eviction (Tightenings #3, #6)
   * Evicts system updates first, never user-edits
   * Blocks new edits if queue is full of user-edits
   */
  private enforceQueueLimits(): void {
    let totalPendingBytes = this.pendingUpdates.reduce((sum, p) => sum + p.update.length, 0);

    // Check pending queue
    while (
      this.pendingUpdates.length > this.MAX_PENDING_UPDATES_COUNT ||
      totalPendingBytes > this.MAX_PENDING_UPDATES_BYTES
    ) {
      // Priority: evict system/init updates first, NEVER user-edits
      const nonUserEditIndex = this.pendingUpdates.findIndex(
        p => String(p.origin) !== 'user-edit'
      );

      if (nonUserEditIndex !== -1) {
        // Safe to evict: system update
        const evicted = this.pendingUpdates.splice(nonUserEditIndex, 1)[0];
        totalPendingBytes -= evicted.update.length;
        this.logTelemetry(TELEMETRY_EVENT_TYPES.QUEUE_LIMIT_EVICTION, {
          updateId: evicted.updateId,
          origin: String(evicted.origin),
          reason: 'system_update_evicted',
          queueCount: this.pendingUpdates.length,
          queueBytes: totalPendingBytes
        });
      } else {
        // Queue is ALL user-edits - cannot safely evict
        console.error(`[YJS-WS] ❌ Queue full of user-edits - BLOCKING NEW EDITS`);
        this.logTelemetry(TELEMETRY_EVENT_TYPES.QUEUE_LIMIT_BLOCKED, {
          queueCount: this.pendingUpdates.length,
          queueBytes: totalPendingBytes,
          reason: 'all_user_edits'
        });
        this.queueFull = true;
        break;
      }
    }

    // Check unacked queue
    let totalUnackedBytes = Array.from(this.unackedUpdates.values())
      .reduce((sum, u) => sum + u.update.length, 0);

    while (
      this.unackedUpdates.size > this.MAX_UNACKED_UPDATES_COUNT ||
      totalUnackedBytes > this.MAX_UNACKED_UPDATES_BYTES
    ) {
      // Find oldest non-user-edit first
      const sorted = Array.from(this.unackedUpdates.entries())
        .sort((a, b) => a[1].sentAt - b[1].sentAt);

      const nonUserEdit = sorted.find(([_, u]) => String(u.origin) !== 'user-edit');

      if (nonUserEdit) {
        // Safe to evict: system update
        this.clearAckTimer(nonUserEdit[0]);  // Clear timer before deleting
        this.unackedUpdates.delete(nonUserEdit[0]);
        totalUnackedBytes -= nonUserEdit[1].update.length;
        this.logTelemetry(TELEMETRY_EVENT_TYPES.QUEUE_LIMIT_EVICTION, {
          updateId: nonUserEdit[0],
          origin: String(nonUserEdit[1].origin),
          reason: 'system_update_evicted',
          queueType: 'unacked',
          queueCount: this.unackedUpdates.size,
          queueBytes: totalUnackedBytes
        });
      } else {
        // Queue is ALL user-edits - cannot safely evict
        console.error(`[YJS-WS] ❌ Unacked queue full of user-edits - BLOCKING NEW EDITS`);
        this.logTelemetry(TELEMETRY_EVENT_TYPES.QUEUE_LIMIT_BLOCKED, {
          queueCount: this.unackedUpdates.size,
          queueBytes: totalUnackedBytes,
          reason: 'all_user_edits',
          queueType: 'unacked'
        });
        this.queueFull = true;
        break;  // Stop eviction loop
      }
    }
  }

  /**
   * Clean up exhausted updates older than 24h (Tightening #1)
   * Prevents unbounded growth of exhausted updates
   */
  private cleanupExhaustedUpdates(): void {
    const MAX_EXHAUSTED_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
    const now = Date.now();

    for (const [updateId, update] of this.unackedUpdates) {
      if (update.exhausted && update.exhaustedAt) {
        const age = now - update.exhaustedAt;
        if (age > MAX_EXHAUSTED_AGE_MS) {
          console.warn(`[YJS-WS] ⏰ Removing exhausted update after 24h: ${updateId}`);
          this.unackedUpdates.delete(updateId);
          this.logTelemetry(TELEMETRY_EVENT_TYPES.EXHAUSTED_UPDATE_EXPIRED, {
            updateId,
            origin: String(update.origin),
            ageMs: age
          });
        }
      }
    }
    this.persistQueues();
  }

  /**
   * Retry previously exhausted updates with jitter (Tightenings #1, #4; Bugfix P2)
   * Called after sync completes
   * Adds random 0-5s delay to prevent thundering herd
   * Prevents duplicate scheduling with exhaustedRetryScheduled guard
   */
  private retryExhaustedUpdates(): void {
    const exhausted = Array.from(this.unackedUpdates.values())
      .filter(u => u.exhausted && !this.exhaustedRetryScheduled.has(u.updateId));  // Bugfix P2: Filter already scheduled

    if (exhausted.length === 0) return;

    console.log(`[YJS-WS] 🔄 Retrying ${exhausted.length} previously exhausted updates with jitter`);

    exhausted.forEach((update) => {
      // Add jitter: 0-5000ms random delay
      const jitterMs = Math.random() * 5000;

      // Bugfix P2: Mark as scheduled to prevent duplicates
      this.exhaustedRetryScheduled.add(update.updateId);

      setTimeout(() => {
        if (this.destroyed || !this._connected) {
          this.exhaustedRetryScheduled.delete(update.updateId);  // Bugfix P2: Clean up on abort
          return;
        }

        // Reset retry state for new attempt
        this.unackedUpdates.set(update.updateId, {
          ...update,
          exhausted: false,
          exhaustedAt: undefined,
          retryCount: 0,  // Fresh start
          sentAt: Date.now()
        });

        this.sendUpdate(update.update, {
          updateId: update.updateId,
          origin: String(update.origin),
          clientTs: update.clientTs,
          retryCount: 0
        });

        this.logTelemetry(TELEMETRY_EVENT_TYPES.EXHAUSTED_RETRY_ATTEMPT, {
          updateId: update.updateId,
          origin: String(update.origin),
          jitterMs: Math.round(jitterMs)
        });

        // Bugfix P2: Remove from scheduled set after retry completes
        this.exhaustedRetryScheduled.delete(update.updateId);
      }, jitterMs);
    });
  }

  /**
   * Show UI warning when queue is full of user-edits (Tightening #6)
   * Called when queueFull flag is set
   */
  private showQueueFullWarning(): void {
    // For now, just console warn - UI integration to be added later
    console.error(`[YJS-WS] ⚠️ CRITICAL: Queue full of unsent user edits - new edits will be rejected`);

    // Emit event that UI can listen to
    this.emit('queue-full', [{
      pendingCount: this.pendingUpdates.length,
      unackedCount: this.unackedUpdates.size,
      message: 'Unable to save changes - please retry when connection is stable'
    }]);
  }

  private emitQueueRecovered(): void {
    this.emit('queue-recovered', [{
      pendingCount: this.pendingUpdates.length,
      unackedCount: this.unackedUpdates.size,
      message: 'Queue recovered'
    }]);
  }

  /**
   * Connect to WebSocket server.
   * If getToken is provided, fetches fresh token first to avoid "Signature has expired" errors.
   */
  connect(): void {
    if (this.destroyed) return;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;

    this.shouldConnect = true;
    console.log(`[YJS-WS] connect() called for room: ${this.roomId}`);

    // If getToken is provided, fetch fresh token before connecting
    if (this.getToken) {
      this.getToken()
        .then((freshToken) => {
          this.token = freshToken;
          this.doConnect();
        })
        .catch((error) => {
          if (this.isForbiddenError(error)) {
            console.error(`[YJS-WS] ❌ Room token forbidden for room: ${this.roomId}`);
            this.shouldConnect = false;
            this.emit('status', [{ status: 'forbidden' }]);
            this.logTelemetry('yjs_error', { message: 'room_token_forbidden' });
            return;
          }

          // Fall back to an existing token only if one is available.
          if (this.token) {
            this.doConnect();
            return;
          }

          this.emit('status', [{ status: 'disconnected' }]);
          this.logTelemetry('yjs_error', { message: 'room_token_fetch_failed' });
          if (this.shouldConnect && !this.destroyed) {
            this.scheduleReconnect();
          }
        });
    } else {
      this.doConnect();
    }
  }

  /**
   * Internal method to establish WebSocket connection (called after token is ready)
   */
  private doConnect(): void {
    if (this.destroyed) return;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;

    const wsUrl = `${this.wsEndpoint}?token=${encodeURIComponent(this.token)}`;
    console.log(`[YJS-WS] Connecting to: ${this.wsEndpoint} (token: ${this.token ? 'present' : 'missing'})`);
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log(`[YJS-WS] ✅ Connected to room: ${this.roomId}`);
      this._connected = true;
      this.currentReconnectDelay = this.reconnectDelay;
      this.emit('status', [{ status: 'connected' }]);
      this.logTelemetry('yjs_connect');

      // Subscribe to the room
      // NOTE: Pending updates are flushed AFTER receiving initial sync (in handleSync)
      // to prevent race condition where client's stale data overwrites server data
      this.send({ action: 'subscribe', roomId: this.roomId });

      // Sync watchdog: re-subscribe every 10s until initial sync arrives, socket closes,
      // or max attempts reached (avoids infinite noise under prolonged backend outage).
      if (this.syncWatchdogTimer) clearInterval(this.syncWatchdogTimer);
      let watchdogAttempts = 0;
      const MAX_WATCHDOG_ATTEMPTS = 6; // 60s total, then rely on reconnect cycle
      this.syncWatchdogTimer = setInterval(() => {
        if (this._synced || this.destroyed || !this._connected || watchdogAttempts >= MAX_WATCHDOG_ATTEMPTS) {
          if (this.syncWatchdogTimer) { clearInterval(this.syncWatchdogTimer); this.syncWatchdogTimer = null; }
          return;
        }
        watchdogAttempts++;
        console.warn(`[YJS-WS] ⚠️ Sync watchdog (${watchdogAttempts}/${MAX_WATCHDOG_ATTEMPTS}): no initial sync — re-subscribing`);
        this.send({ action: 'subscribe', roomId: this.roomId });
      }, 10000);

      // Start ping interval
      this.startPing();

      // Send local awareness if set
      if (this.localAwarenessState) {
        this.sendAwareness(this.localAwarenessState);
      }
    };

    this.ws.onclose = (event) => {
      console.log(`[YJS-WS] ❌ Disconnected from room: ${this.roomId}, code: ${event.code}, reason: ${event.reason || 'none'}, was synced: ${this._synced}, pending updates: ${this.pendingUpdates.length}`);
      this._connected = false;
      this._synced = false;
      this.stopPing();
      if (event.code === 4003) {
        this.shouldConnect = false;
      }
      this.emit('status', [{ status: 'disconnected' }]);
      this.logTelemetry('yjs_disconnect', {
        code: event.code,
        reason: event.reason || 'none',
      });

      // Reconnect if we should still be connected
      if (this.shouldConnect && !this.destroyed) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error(`[YJS-WS] ⚠️ WebSocket error for room: ${this.roomId}`, error);
      this.emit('connection-error', [error]);
      this.logTelemetry('yjs_error', { message: 'websocket_error' });
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.log(`[YJS-WS] 🔌 Disconnecting from room: ${this.roomId}, pending updates: ${this.pendingUpdates.length}`);
    this.shouldConnect = false;
    this.stopPing();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.syncWatchdogTimer) {
      clearInterval(this.syncWatchdogTimer);
      this.syncWatchdogTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this._connected = false;
    this._synced = false;

    // Requeue unacked updates on disconnect (delivery is uncertain without ACK)
    if (this.unackedUpdates.size > 0) {
      const requeued = Array.from(this.unackedUpdates.values()).map((u) => ({
        update: u.update,
        origin: u.origin,
        updateId: u.updateId,
        timestamp: u.timestamp,
        clientTs: u.clientTs,
        retryCount: u.retryCount,
      }));
      this.pendingUpdates = [...requeued, ...this.pendingUpdates];
      this.ackTimers.forEach((timer) => clearTimeout(timer));
      this.ackTimers.clear();
      this.unackedUpdates.clear();
      this.logTelemetry('yjs_unacked_requeued', {
        count: requeued.length,
        reason: 'socket_disconnect',
      });
      this.persistQueues();
    }
  }

  /**
   * Destroy the provider
   */
  destroy(): void {
    console.log(`[YJS-WS] 💥 Destroying provider for room: ${this.roomId}, pending updates being discarded: ${this.pendingUpdates.length}`);
    this.destroyed = true;
    this.disconnect();
    this.ydoc.off('update', this.updateHandler);
    this.unregisterBeforeUnloadGuard();
    // Clear pending updates to prevent memory leaks
    this.pendingUpdates = [];
    this.ackTimers.forEach((timer) => clearTimeout(timer));
    this.ackTimers.clear();
    this.unackedUpdates.clear();
    super.destroy();
  }

  /**
   * Manually publish a local Y.Doc update when ydoc.on('update') doesn't fire.
   * Delegates to the existing updateHandler — same send/queue/telemetry logic.
   * Used as belt-and-suspenders in multi-tab scenarios where transact() produces
   * no events despite Y.Doc state changing.
   */
  publishLocalUpdate(update: Uint8Array, origin: any): void {
    if (this.destroyed) return;
    this.updateHandler(update, origin);
  }

  /**
   * Update the JWT token and reconnect if needed
   * Called when token is refreshed to prevent "Signature has expired" errors
   */
  updateToken(newToken: string): void {
    if (this.token === newToken) return;
    console.log(`[YJS-WS] 🔑 Token updated for room: ${this.roomId}, will reconnect: ${this.shouldConnect && !this.destroyed}`);
    this.token = newToken;

    // Reconnect with the new token if we should be connected
    if (this.shouldConnect && !this.destroyed) {
      this.disconnect();
      // Small delay to ensure clean disconnect
      setTimeout(() => {
        if (!this.destroyed) {
          this.shouldConnect = true;
          this.connect();
        }
      }, 100);
    }
  }

  /**
   * Set local awareness state
   */
  setLocalAwareness(state: any): void {
    this.localAwarenessState = state;
    if (this._connected) {
      this.sendAwareness(state);
    }
  }

  /**
   * Get awareness states
   */
  getAwareness(): Map<string, any> {
    return this.awareness;
  }

  /**
   * Send a message over WebSocket.
   * @returns true if message was sent, false if socket not open
   */
  private send(message: object): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;  // Socket not open, send failed
    }
    this.ws.send(JSON.stringify(message));
    return true;  // Successfully sent
  }

  private sendUpdate(update: Uint8Array, telemetry?: { updateId?: string; origin?: string; clientTs?: string; retryCount?: number }): void {
    // AWS API Gateway has a 128KB message limit, use 100KB to be safe
    const MAX_CHUNK_SIZE = 100 * 1024; // 100KB

    // Log with warning for large updates (potential issue indicator)
    if (update.length > 50000) {
      console.log(`[YJS-WS] 📤 ⚠️ Sending LARGE update (${update.length} bytes) for room: ${this.roomId} - investigate if this happens frequently`);
    } else {
      console.log(`[YJS-WS] 📤 Sending update (${update.length} bytes) for room: ${this.roomId}`);
    }

    if (update.length <= MAX_CHUNK_SIZE) {
      // Small update - send directly
      const base64 = this.uint8ArrayToBase64(update);
      const sent = this.send({
        action: 'update',
        roomId: this.roomId,
        updateId: telemetry?.updateId,
        update: base64
      });
      if (!sent) {
        throw new Error('WebSocket not open - send failed');
      }
      // Log telemetry AFTER successful send
      this.logTelemetry('yjs_update_sent', {
        updateId: telemetry?.updateId,
        clientTs: telemetry?.clientTs,
        origin: telemetry?.origin,
        bytes: update.length,
        chunked: false
      });
      this.markAsUnacked(update, telemetry);
    } else {
      // Large update - split into chunks to avoid "Message too big" error
      console.log(`[YJS-WS] 📤 Splitting large update into ${Math.ceil(update.length / MAX_CHUNK_SIZE)} chunks`);
      const chunks = this.splitIntoChunks(update, MAX_CHUNK_SIZE);
      const chunkId = this.generateChunkId();

      for (let i = 0; i < chunks.length; i++) {
        const sent = this.send({
          action: 'update-chunk',
          roomId: this.roomId,
          updateId: telemetry?.updateId,
          chunkId,
          chunkIndex: i,
          totalChunks: chunks.length,
          chunk: this.uint8ArrayToBase64(chunks[i])
        });
        if (!sent) {
          throw new Error(`WebSocket not open - chunk ${i + 1}/${chunks.length} send failed`);
        }
      }

      // Log telemetry AFTER all chunks successfully sent
      this.logTelemetry('yjs_update_sent', {
        updateId: telemetry?.updateId,
        clientTs: telemetry?.clientTs,
        origin: telemetry?.origin,
        bytes: update.length,
        chunked: true,
        chunkCount: chunks.length
      });
      this.markAsUnacked(update, telemetry);
    }
  }

  private splitIntoChunks(data: Uint8Array, chunkSize: number): Uint8Array[] {
    const chunks: Uint8Array[] = [];
    for (let i = 0; i < data.length; i += chunkSize) {
      chunks.push(data.slice(i, Math.min(i + chunkSize, data.length)));
    }
    return chunks;
  }

  private generateChunkId(): string {
    // Generate a unique ID for this chunk batch
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private sendAwareness(state: any): void {
    this.send({
      action: 'awareness',
      roomId: this.roomId,
      awareness: state
    });
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      switch (message.action) {
        case 'sync':
          this.handleSync(message);
          break;
        case 'awareness':
          this.handleAwareness(message);
          break;
        case 'pong':
          // Ping acknowledged
          break;
        case 'update_ack':
          this.handleUpdateAck(message);
          break;
        case 'error':
          console.error(`[YJS-WS] Server error for room ${this.roomId}: ${message.error || 'unknown'}`);
          break;
        default:
          // Ignore unknown messages
          break;
      }
    } catch (error) {
      // Ignore parse errors
    }
  }

  private handleSync(message: { type: string; roomId: string; state?: string; update?: string; source?: string }): void {
    if (message.type === 'initial') {
      console.log(`[YJS-WS] 🔄 Received initial sync for room: ${this.roomId}`);
      this.syncStartTime = Date.now();
      this.logTelemetry('yjs_sync_start', { pendingUpdatesCount: this.pendingUpdates.length });

      // FIX B v2: SELECTIVE DISCARD - Preserve user edits, discard only system updates
      // User edits (origin='user-edit') must be preserved and replayed after sync
      // System updates (origin='initialize', etc.) are stale and should be discarded
      if (this.pendingUpdates.length > 0) {
        // SELECTIVE DISCARD: Separate user edits from system updates
        const toKeep = this.pendingUpdates.filter(p => p.origin === 'user-edit');
        const toDiscard = this.pendingUpdates.filter(p => p.origin !== 'user-edit');

        // Calculate metrics for discarded items only
        const totalDiscardedBytes = toDiscard.reduce((sum, p) => sum + p.update.length, 0);
        const originBreakdown = toDiscard.reduce((acc, p) => {
          const origin = String(p.origin);
          acc[origin] = (acc[origin] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        if (toDiscard.length > 0) {
          console.log(
            `[YJS-WS] ⚠️ DISCARDING ${toDiscard.length} stale system updates ` +
            `(${totalDiscardedBytes} bytes total) - PRESERVING ${toKeep.length} user edits`
          );

          this.logTelemetry(TELEMETRY_EVENT_TYPES.PENDING_UPDATE_DISCARDED, {
            updateId: `batch_${this.tabId}_${Date.now()}_${toDiscard.length}`,
            origin: 'mixed',
            reason: 'initial_sync_discard',
            count: toDiscard.length,
            totalBytes: totalDiscardedBytes,
            userEditCount: 0,
            systemUpdateCount: toDiscard.length,
            keptUserEdits: toKeep.length,
            origins: Object.keys(originBreakdown),
            originBreakdown
          });

          toDiscard.forEach((pending) => {
            this.logTelemetry('yjs_update_discarded', {
              updateId: pending.updateId,
              clientTs: pending.clientTs,
              origin: String(pending.origin),
              ageMs: Date.now() - pending.timestamp,
              reason: 'initial_sync_discard'
            });
          });
        }

        if (toKeep.length > 0) {
          console.log(`[YJS-WS] ✅ PRESERVING ${toKeep.length} user-edit updates for replay after sync`);
          this.logTelemetry('yjs_pending_update_preserved', {
            count: toKeep.length,
            totalBytes: toKeep.reduce((sum, p) => sum + p.update.length, 0),
            reason: 'user_edit_protection'
          });
        }

        // CRITICAL: Keep user edits for replay
        this.pendingUpdates = toKeep;
        this.persistQueues();
      }

      // Apply server Y.Doc state as the source of truth
      let stateBytes = 0;
      if (message.state) {
        const serverState = this.base64ToUint8Array(message.state);
        stateBytes = serverState.length;
        console.log(`[YJS-WS] 📥 Applying server state (${serverState.length} bytes) as source of truth for room: ${this.roomId}`);
        Y.applyUpdate(this.ydoc, serverState, 'remote');
        this.checkCommentChanges('initial_sync');
      } else {
        console.log(`[YJS-WS] 📥 No server state received (new/empty document) for room: ${this.roomId}`);
      }

      // CRITICAL: Set synced state BEFORE anything that can throw (remote-update emit, replay).
      // Previously, emit('sync', [true]) was after replay — React `synced` stayed false if replay threw.
      this._synced = true;
      this.emit('sync', [true]);
      console.log(`[YJS-WS] ✅ Initial sync complete - now synced`);
      this.logTelemetry('yjs_sync_complete', {
        syncDurationMs: Date.now() - this.syncStartTime,
        stateBytes,
        wasEmpty: !message.state,
      });

      // Clear sync watchdog timer — initial sync arrived
      if (this.syncWatchdogTimer) {
        clearInterval(this.syncWatchdogTimer);
        this.syncWatchdogTimer = null;
      }

      // Non-critical block 1: Emit remote-update for UI hydration (isolated try-catch)
      if (message.state) {
        try {
          this.emit('remote-update', [{ source: 'initial-sync', size: stateBytes }]);
        } catch (e) {
          console.error('[YJS-WS] ❌ remote-update emit failed after initial sync:', e);
        }
      }

      // Non-critical block 2: Replay preserved user edits (isolated try-catch)
      // Failures here must NOT flip _synced back to false.
      try {
      if (this.pendingUpdates.length > 0) {
        console.log(`[YJS-WS] 🔄 Replaying ${this.pendingUpdates.length} preserved user edits`);

        const toReplay = [...this.pendingUpdates];
        const successfullyReplayed: string[] = [];
        const failedReplays: typeof toReplay = [];

        for (const pending of toReplay) {
          if (!this._connected || !this._synced) {
            console.warn(`[YJS-WS] ⚠️ Connection lost during replay, stopping`);
            failedReplays.push(pending);

            this.logTelemetry('yjs_update_replay_blocked', {
              updateId: pending.updateId,
              reason: 'connection_lost_during_replay',
              connected: this._connected,
              synced: this._synced
            });
            continue;
          }

          try {
            console.log(`[YJS-WS] 🔄 Replaying: updateId=${pending.updateId}, bytes=${pending.update.length}`);
            this.sendUpdate(pending.update, {
              updateId: pending.updateId,
              origin: String(pending.origin),
              clientTs: pending.clientTs,
              retryCount: pending.retryCount
            });

            successfullyReplayed.push(pending.updateId);

            this.logTelemetry('yjs_update_replayed', {
              updateId: pending.updateId,
              clientTs: pending.clientTs,
              origin: String(pending.origin),
              bytes: pending.update.length,
              ageMs: Date.now() - pending.timestamp,
              reason: 'post_sync_replay'
            });
          } catch (error) {
            console.error(`[YJS-WS] ❌ Replay failed for updateId=${pending.updateId}:`, error);
            failedReplays.push(pending);

            this.logTelemetry('yjs_update_replay_failed', {
              updateId: pending.updateId,
              reason: 'send_error',
              error: String(error)
            });
          }
        }

        const sentIds = new Set(successfullyReplayed);
        this.pendingUpdates = this.pendingUpdates.filter(
          p => !sentIds.has(p.updateId)
        );
        this.persistQueues();

        if (this.queueFull && this.hasCapacity()) {
          this.queueFull = false;
          console.log(`[YJS-WS] ✅ Queue recovered after sync replay - resuming user edits`);
          this.emitQueueRecovered();
        }

        if (failedReplays.length === 0) {
          console.log(`[YJS-WS] ✅ Successfully replayed all ${successfullyReplayed.length} user edits`);
          this.logTelemetry('yjs_replay_complete', {
            count: successfullyReplayed.length,
            totalBytes: toReplay.reduce((sum, p) => sum + p.update.length, 0),
            failedCount: 0
          });
        } else {
          console.warn(`[YJS-WS] ⚠️ Replay incomplete: ${successfullyReplayed.length} succeeded, ${failedReplays.length} failed (requeued)`);
          this.logTelemetry('yjs_replay_partial', {
            successCount: successfullyReplayed.length,
            failedCount: failedReplays.length,
            requeuedUpdateIds: failedReplays.map(p => p.updateId)
          });
        }
      }

      this.retryExhaustedUpdates();
      this.cleanupExhaustedUpdates();
      } catch (e) {
        console.error('[YJS-WS] ❌ Replay/cleanup failed after initial sync (synced state preserved):', e);
      }
    } else if (message.type === 'update') {
      // Incremental update from another client
      if (message.update) {
        const update = this.base64ToUint8Array(message.update);
        console.log(`[YJS-WS] 📥 Received remote update (${update.length} bytes) from: ${message.source || 'peer'} for room: ${this.roomId}`);
        Y.applyUpdate(this.ydoc, update, 'remote');

        // Fix 2: Implicit sync — promote to synced only after Y.applyUpdate succeeds.
        // If we successfully applied a peer update, we ARE synced. Missing the formal
        // initial handshake shouldn't freeze the tab.
        if (!this._synced) {
          console.warn('[YJS-WS] ⚠️ Received incremental update before initial sync — forcing synced state');
          this._synced = true;
          this.emit('sync', [true]);
          // Reuse existing accepted telemetry event — no new event type needed
          this.logTelemetry('yjs_sync_complete', {
            syncDurationMs: this.syncStartTime > 0 ? Date.now() - this.syncStartTime : -1,
            stateBytes: update.length,
            wasEmpty: false,
            trigger: 'implicit_sync_on_incremental_update',
          });
          // Clear sync watchdog timer
          if (this.syncWatchdogTimer) {
            clearInterval(this.syncWatchdogTimer);
            this.syncWatchdogTimer = null;
          }
        }
        this.logTelemetry('yjs_update_applied_remote', {
          source: message.source || 'peer',
          bytes: update.length
        });
        this.checkCommentChanges('remote_update_' + (message.source || 'peer'));
        this.emit('remote-update', [{ source: message.source || 'peer', size: update.length }]);
      }
    }
  }

  private handleAwareness(message: { roomId: string; data: any; from?: string }): void {
    if (message.from) {
      this.awareness.set(message.from, message.data);
      this.emit('awareness', [this.awareness]);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    console.log(`[YJS-WS] Scheduling reconnect in ${this.currentReconnectDelay}ms for room: ${this.roomId}`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.currentReconnectDelay);

    // Exponential backoff
    this.currentReconnectDelay = Math.min(
      this.currentReconnectDelay * 2,
      this.maxReconnectDelay
    );
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ action: 'ping' });
    }, this.pingInterval);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Check for comment/notes field changes after Y.applyUpdate.
   * Compares current Y.Doc state against cached values and logs diffs.
   */
  private checkCommentChanges(trigger: string): void {
    try {
      const yRows = this.ydoc.getMap<Y.Map<any>>('rows');
      const changes: Array<{ rowId: string; field: string; old: string | null; new: string | null }> = [];

      yRows.forEach((yRow: Y.Map<any>, rowId: string) => {
        const ourComment = yRow.get('ourComment') ?? null;
        const notes = yRow.get('notes') ?? null;
        const cached = this.commentCache.get(rowId);

        if (cached) {
          if (cached.ourComment !== ourComment) {
            changes.push({ rowId, field: 'ourComment', old: cached.ourComment, new: ourComment });
          }
          if (cached.notes !== notes) {
            changes.push({ rowId, field: 'notes', old: cached.notes, new: notes });
          }
        }
        this.commentCache.set(rowId, { ourComment, notes });
      });

      if (changes.length > 0) {
        const cleared = changes.filter(c => c.old && !c.new);
        console.warn(`[YJS-WS] ⚠️ Comment changes after ${trigger}: ${changes.length} total, ${cleared.length} CLEARED`, changes);
        this.logTelemetry('yjs_comment_change_detected', {
          trigger,
          totalChanges: changes.length,
          cleared: cleared.length
        });
        changes.slice(0, 20).forEach((change) => {
          this.logTelemetry('yjs_comment_field_changed', {
            trigger,
            rowId: change.rowId,
            field: change.field,
            beforeHash: this.hashString(change.old),
            afterHash: this.hashString(change.new),
            beforeLen: change.old ? change.old.length : 0,
            afterLen: change.new ? change.new.length : 0
          });
        });
      }
    } catch (e) {
      // Don't let tracking break sync flow
    }
  }

  private hashString(value: string | null): string {
    const input = value || '';
    let hash = 2166136261;
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(16);
  }

  private handleUpdateAck(message: { roomId?: string; updateId?: string; serverTs?: string }): void {
    const updateId = message.updateId;
    if (!updateId) return;

    const unacked = this.unackedUpdates.get(updateId);
    if (!unacked) return;

    console.log(
      `[YJS-WS] ✅ ACK received for updateId=${updateId} ` +
      `(latency=${Date.now() - unacked.sentAt}ms, retry=${unacked.retryCount})`
    );

    this.clearAckTimer(updateId);
    this.unackedUpdates.delete(updateId);
    this.exhaustedRetryScheduled.delete(updateId);  // Bugfix P2: Clean up scheduled tracking
    this.logTelemetry('yjs_update_acked', {
      updateId,
      origin: String(unacked.origin),
      bytes: unacked.update.length,
      ackLatencyMs: Date.now() - unacked.sentAt,
      retryCount: unacked.retryCount,
      serverTs: message.serverTs,
    });
    this.persistQueues();

    // Bugfix P1: Reset queueFull if queues have recovered capacity
    if (this.queueFull && this.hasCapacity()) {
      this.queueFull = false;
      console.log(`[YJS-WS] ✅ Queue recovered - resuming user edits`);
      this.emitQueueRecovered();
    }
  }

  private markAsUnacked(update: Uint8Array, telemetry?: { updateId?: string; origin?: string; clientTs?: string; retryCount?: number }): void {
    const updateId = telemetry?.updateId;
    if (!updateId) return;
    const retryCount = telemetry?.retryCount ?? 0;

    this.clearAckTimer(updateId);

    this.unackedUpdates.set(updateId, {
      update,
      origin: telemetry?.origin ?? 'unknown',
      updateId,
      timestamp: Date.now(),
      clientTs: telemetry?.clientTs || new Date().toISOString(),
      sentAt: Date.now(),
      retryCount,
    });
    this.persistQueues();

    // ACK timeout handler with soft/hard retry strategy (Tightenings #1, #2, #8)
    const timer = setTimeout(() => {
      const stillUnacked = this.unackedUpdates.get(updateId);
      if (!stillUnacked || this.destroyed) return;

      const nextRetryCount = stillUnacked.retryCount + 1;

      // Check if exhausted (Tightening #1)
      if (nextRetryCount > this.MAX_TOTAL_RETRIES) {
        this.markAsExhausted(updateId, stillUnacked);
        return;
      }

      // Determine retry strategy (Tightening #2)
      const isSoftRetry = nextRetryCount <= this.MAX_SOFT_RETRIES;
      const backoffMs = this.ackTimeoutMs * Math.pow(2, stillUnacked.retryCount);

      this.unackedUpdates.set(updateId, {
        ...stillUnacked,
        retryCount: nextRetryCount,
      });

      this.logTelemetry(TELEMETRY_EVENT_TYPES.ACK_TIMEOUT, {
        updateId,
        origin: String(stillUnacked.origin),
        timeoutMs: this.ackTimeoutMs,
        retryCount: nextRetryCount,
        retryType: isSoftRetry ? 'soft' : 'hard',
        nextRetryInMs: isSoftRetry ? 0 : backoffMs
      });

      if (isSoftRetry) {
        // Soft retry: resend on SAME connection (Tightening #2)
        console.log(`[YJS-WS] 🔄 Soft retry ${nextRetryCount}/${this.MAX_SOFT_RETRIES} - resending without reconnect`);

        if (this._connected && this._synced) {
          this.sendUpdate(stillUnacked.update, {
            updateId,
            origin: String(stillUnacked.origin),
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
        } else {
          // Connection lost during soft retry, move to pending
          this.clearAckTimer(updateId);
          this.unackedUpdates.delete(updateId);
          this.pendingUpdates.unshift({
            update: stillUnacked.update,
            origin: stillUnacked.origin,
            updateId,
            timestamp: stillUnacked.timestamp,
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
          this.persistQueues();
        }
      } else {
        // Hard retry: force reconnect (Tightening #2)
        console.log(`[YJS-WS] 🔌 Hard retry ${nextRetryCount}/${this.MAX_TOTAL_RETRIES} - forcing reconnect after ${backoffMs}ms`);

        if (this._connected) {
          // Socket is up - disconnect and schedule reconnect
          this.disconnect();
          this.shouldConnect = true;

          setTimeout(() => {
            if (!this.destroyed && this.shouldConnect) {
              this.connect();
            }
          }, backoffMs);
        } else {
          // Socket already down - move to pending queue for next connection (Bugfix Round 2 #1)
          console.warn(`[YJS-WS] ⚠️ Hard retry timeout fired but already disconnected - moving to pending queue`);
          this.clearAckTimer(updateId);
          this.unackedUpdates.delete(updateId);
          this.pendingUpdates.unshift({
            update: stillUnacked.update,
            origin: stillUnacked.origin,
            updateId,
            timestamp: stillUnacked.timestamp,
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
          this.persistQueues();

          // Schedule reconnect if shouldConnect is true
          if (this.shouldConnect && !this.destroyed) {
            setTimeout(() => {
              if (!this.destroyed && this.shouldConnect) {
                this.connect();
              }
            }, backoffMs);
          }
        }
      }
    }, this.ackTimeoutMs);
    this.ackTimers.set(updateId, timer);
  }

  private clearAckTimer(updateId: string): void {
    const timer = this.ackTimers.get(updateId);
    if (!timer) return;
    clearTimeout(timer);
    this.ackTimers.delete(updateId);
  }

  private getUnsavedCount(): number {
    return this.pendingUpdates.length + this.unackedUpdates.size;
  }

  private registerBeforeUnloadGuard(): void {
    if (typeof window === 'undefined' || this.beforeUnloadHandler) return;
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      const unsaved = this.getUnsavedCount();
      if (unsaved <= 0) return;
      event.preventDefault();
      event.returnValue = `You have ${unsaved} unsaved collaboration update(s).`;
      this.logTelemetry('yjs_refresh_blocked', {
        pendingCount: this.pendingUpdates.length,
        unackedCount: this.unackedUpdates.size,
      });
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private unregisterBeforeUnloadGuard(): void {
    if (typeof window === 'undefined' || !this.beforeUnloadHandler) return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  private persistQueues(): void {
    if (typeof window === 'undefined') return;
    if (this.queuePersistTimer) clearTimeout(this.queuePersistTimer);
    this.queuePersistTimer = setTimeout(() => {
      void this.persistQueuesNow();
    }, 250);
  }

  private async persistQueuesNow(): Promise<void> {
    try {
      const db = await this.openQueueDb();
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      const store = tx.objectStore(this.queueStoreName);
      const payload = {
        roomId: this.roomId,
        updatedAt: Date.now(),
        pending: this.pendingUpdates.map((p) => ({
          ...p,
          update: this.uint8ArrayToBase64(p.update),
        })),
        unacked: Array.from(this.unackedUpdates.values()).map((u) => ({
          ...u,
          update: this.uint8ArrayToBase64(u.update),
        })),
      };
      store.put(payload);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    } catch {
      // best effort only
    }
  }

  private async loadPersistedQueues(): Promise<void> {
    try {
      const db = await this.openQueueDb();
      const tx = db.transaction(this.queueStoreName, 'readonly');
      const store = tx.objectStore(this.queueStoreName);
      const record = await new Promise<any>((resolve, reject) => {
        const req = store.get(this.roomId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      if (!record) return;

      const restoredPending: PendingUpdate[] = (record.pending || []).map((p: any) => ({
        ...p,
        update: this.base64ToUint8Array(p.update),
        retryCount: Number.isFinite(p.retryCount) ? p.retryCount : 0,
      }));
      const restoredUnacked: UnackedUpdate[] = (record.unacked || []).map((u: any) => ({
        ...u,
        update: this.base64ToUint8Array(u.update),
        retryCount: Number.isFinite(u.retryCount) ? u.retryCount : 0,
      }));

      // Treat restored unacked as pending to safely replay after reconnect.
      this.pendingUpdates = [...restoredUnacked.map((u) => ({
        update: u.update,
        origin: u.origin,
        updateId: u.updateId,
        timestamp: u.timestamp,
        clientTs: u.clientTs,
        retryCount: Number.isFinite(u.retryCount) ? u.retryCount : 0,
      })), ...restoredPending];
      this.unackedUpdates.clear();

      if (this.pendingUpdates.length > 0) {
        this.logTelemetry('yjs_pending_queue_restored', {
          count: this.pendingUpdates.length,
          source: 'indexeddb',
        });
      }
    } catch {
      // best effort only
    }
  }

  private openQueueDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = window.indexedDB.open(this.queueDbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.queueStoreName)) {
          db.createObjectStore(this.queueStoreName, { keyPath: 'roomId' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  public async logTelemetry(eventType: string, meta?: Record<string, any>): Promise<void> {
    try {
      if (typeof window === 'undefined') return;

      // Validate event type is in allowed list (Tightening #7)
      const allowedTypes = Object.values(TELEMETRY_EVENT_TYPES);
      if (!allowedTypes.includes(eventType as any)) {
        console.error(`[YJS-WS] ❌ Invalid telemetry event type: ${eventType} - not in frozen contract`);
        return;
      }

      let clientId = this.telemetry?.clientId;
      let sheetId = this.telemetry?.sheetId;
      if ((!clientId || !sheetId) && this.roomId.includes(':')) {
        const [roomClientId, roomSheetId] = this.roomId.split(':');
        clientId = clientId || roomClientId;
        sheetId = sheetId || roomSheetId;
      }

      if (!clientId || !sheetId) return;

      const body: Record<string, any> = {
        eventType,
        schemaVersion: TELEMETRY_SCHEMA_VERSION,  // Add schema version (Tightening #7)
        clientId,
        sheetId,
        roomId: this.roomId,
        userId: this.telemetry?.userId,
        userEmail: this.telemetry?.userEmail,
        tabId: this.tabId,
        sessionId: this.sessionId,
        clientTs: meta?.clientTs || new Date().toISOString(),
        eventSeq: ++this.eventSeq,
      };

      const topLevelKeys = [
        'updateId', 'origin', 'action', 'bytes', 'queueSize', 'count',
        'userEditCount', 'systemUpdateCount', 'syncDurationMs', 'stateBytes',
        'wasEmpty', 'code', 'reason', 'source', 'trigger', 'totalChanges',
        'cleared', 'rowId', 'field', 'beforeHash', 'afterHash', 'beforeLen',
        'afterLen', 'ageMs', 'chunked', 'chunkCount', 'pendingUpdatesCount',
        'ackLatencyMs', 'pendingCount', 'unackedCount', 'timeoutMs', 'serverTs',
        'retryCount', 'maxRetries',
      ];
      topLevelKeys.forEach((key) => {
        if (meta && meta[key] !== undefined) body[key] = meta[key];
      });

      const residualMeta: Record<string, any> = {};
      if (meta) {
        Object.keys(meta).forEach((key) => {
          if (key !== 'clientTs' && !topLevelKeys.includes(key)) {
            residualMeta[key] = meta[key];
          }
        });
      }
      if (Object.keys(residualMeta).length > 0) {
        body.meta = residualMeta;
      }

      await API.post('brief', '/briefs/telemetry/yjs', { body });

      if (this.telemetrySendFailures > 0 && eventType !== 'yjs_telemetry_delivery_summary') {
        const failedCount = this.telemetrySendFailures;
        this.telemetrySendFailures = 0;
        await API.post('brief', '/briefs/telemetry/yjs', {
          body: {
            eventType: 'yjs_telemetry_delivery_summary',
            clientId,
            sheetId,
            roomId: this.roomId,
            userId: this.telemetry?.userId,
            userEmail: this.telemetry?.userEmail,
            tabId: this.tabId,
            sessionId: this.sessionId,
            clientTs: new Date().toISOString(),
            eventSeq: ++this.eventSeq,
            failedSendCount: failedCount,
          },
        });
      }
    } catch (error) {
      this.telemetrySendFailures += 1;
      // Swallow telemetry errors to avoid impacting sync flow.
    }
  }
}
